
import React, { useState, useEffect } from 'react';
import { Menu, X, User as UserIcon, LogOut, BookOpen, PenTool, CheckCircle2, Shield, Moon, Sun, Fingerprint, ShieldAlert, Users } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext.tsx';
import AuthModal from './AuthModal.tsx';

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { currentUser, logout, theme, toggleTheme, institutionalCALogo, hasPermission, posts } = useApp();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const pendingCount = posts.filter(p => p.status === 'pending').length;

  const publicLinks = [
    { name: 'Services', href: '/#services' },
    { name: 'Process', href: '/#process' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/#contact' },
  ];

  const authorLinks = [
    { name: 'Knowledge Hub', href: '/blog', icon: BookOpen },
    { name: 'Write Insight', href: '/create-post', icon: PenTool },
  ];

  if (hasPermission('CAN_APPROVE_POSTS')) {
    authorLinks.push({ name: 'Moderation', href: '/moderation', icon: ShieldAlert } as any);
  }

  const handleLinkClick = (href: string) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith('/#')) {
      const id = href.split('#')[1];
      if (location.pathname === '/') {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200 py-4 shadow-sm' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-indigo-700 to-indigo-500 flex items-center justify-center font-display font-black text-white text-base shadow-lg shadow-indigo-600/30 group-hover:scale-105 transition-all border border-white/20">
                MP
              </div>
              <div className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 border-2 border-indigo-100 overflow-hidden w-6 h-6 flex items-center justify-center shadow-sm">
                <img src={institutionalCALogo} alt="CA Logo" className="w-full h-full object-contain" />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="font-display font-bold text-lg text-slate-900 hidden sm:block tracking-tight leading-none">CA Manthan Paliwal</span>
              </div>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {!currentUser ? (
              publicLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => handleLinkClick(link.href)}
                  className={`transition-all text-sm font-semibold tracking-wide ${
                    (location.pathname + location.hash) === link.href || (location.pathname === '/' && link.href === '/')
                      ? 'text-indigo-600'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {link.name}
                </Link>
              ))
            ) : (
              authorLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`flex items-center gap-2 transition-all text-sm font-bold tracking-wide relative ${
                    location.pathname === link.href ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  <link.icon size={16} />
                  {link.name}
                  {link.name === 'Moderation' && pendingCount > 0 && (
                    <span className="absolute -top-1.5 -right-2 w-4 h-4 bg-red-500 text-white text-[8px] font-black flex items-center justify-center rounded-full border border-white">
                      {pendingCount}
                    </span>
                  )}
                </Link>
              ))
            )}
            
            <div className="h-6 w-px bg-slate-200 mx-2" />

            {currentUser ? (
              <div className="flex items-center gap-4">
                <Link to="/profile" className="flex items-center gap-2 text-slate-700 hover:text-indigo-600 transition-colors group">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm relative overflow-hidden">
                    {currentUser.avatar ? (
                      <img src={currentUser.avatar} alt={currentUser.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                    ) : (
                      <UserIcon size={18} />
                    )}
                  </div>
                  <span className="text-sm font-bold">{currentUser.name.split(' ')[0]}</span>
                </Link>
                <button 
                  onClick={() => { logout(); navigate('/'); }}
                  className="px-4 py-2 bg-slate-100 hover:bg-red-50 text-slate-600 hover:text-red-600 transition-all rounded-xl text-[10px] font-black uppercase tracking-widest border border-slate-200"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsAuthModalOpen(true)}
                  className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center gap-2 shadow-sm"
                >
                  <Fingerprint size={14} />
                  Member Login
                </button>
                <Link
                  to="/#contact"
                  onClick={() => handleLinkClick('/#contact')}
                  className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/20 flex items-center gap-2"
                >
                  <Users size={14} />
                  Join Network
                </Link>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 md:hidden">
            <button
              className="text-slate-900 p-2 bg-white border border-slate-200 rounded-xl"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-200 p-8 flex flex-col gap-6 animate-in fade-in slide-in-from-top-4 shadow-xl">
            {!currentUser ? (
              publicLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-xl font-bold tracking-tight ${
                    (location.pathname + location.hash) === link.href ? 'text-indigo-600' : 'text-slate-700'
                  }`}
                  onClick={() => handleLinkClick(link.href)}
                >
                  {link.name}
                </Link>
              ))
            ) : (
              authorLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-xl font-bold tracking-tight relative ${
                    location.pathname === link.href ? 'text-indigo-600' : 'text-slate-700'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="flex items-center gap-3">
                    <link.icon size={20} />
                    {link.name}
                    {link.name === 'Moderation' && pendingCount > 0 && (
                      <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full">
                        {pendingCount}
                      </span>
                    )}
                  </div>
                </Link>
              ))
            )}
            
            {currentUser && (
               <Link
                to="/profile"
                className="text-xl font-bold text-indigo-600"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                My Dashboard
              </Link>
            )}

            {currentUser ? (
              <button
                onClick={() => { logout(); setIsMobileMenuOpen(false); navigate('/'); }}
                className="text-xl font-bold text-red-500 text-left"
              >
                Logout
              </button>
            ) : (
              <button
                onClick={() => { setIsMobileMenuOpen(false); setIsAuthModalOpen(true); }}
                className="text-xl font-bold text-slate-700 text-left flex items-center gap-3"
              >
                <Fingerprint size={20} />
                Sign In
              </button>
            )}
          </div>
        )}
      </nav>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </>
  );
};

export default Navbar;
